import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ShoppingBag, Filter, Heart, ShoppingCart, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CATEGORIES = [
  { id: "all", labelKey: "store.allProducts" },
  { id: "tshirt", labelKey: "store.tshirts" },
  { id: "poster", labelKey: "store.posters" },
  { id: "collectible", labelKey: "store.collectibles" },
  { id: "action_figure", labelKey: "store.actionFigures" },
  { id: "membership", labelKey: "store.memberships" },
];

const StorePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [universes, setUniverses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [wishlist, setWishlist] = useState([]);
  
  const category = searchParams.get("category") || "all";
  const universeFilter = searchParams.get("universe");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodRes, uniRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/universes`),
        ]);
        setProducts(prodRes.data);
        setUniverses(uniRes.data);
        
        if (user) {
          try {
            const wishRes = await axios.get(`${API}/wishlist`);
            setWishlist(wishRes.data.map(w => w.product_id));
          } catch (e) {}
        }
      } catch (error) {
        toast.error("Failed to load products");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const filteredProducts = products.filter(p => {
    if (category !== "all" && p.category !== category) return false;
    if (universeFilter && p.universe_id !== universeFilter) return false;
    if (searchTerm && !p.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const featuredProducts = products.filter(p => p.featured);

  const addToWishlist = async (productId) => {
    if (!user) {
      toast.error("Please sign in to add to wishlist");
      return;
    }
    try {
      await axios.post(`${API}/wishlist`, { product_id: productId });
      setWishlist([...wishlist, productId]);
      toast.success("Added to wishlist");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("Already in wishlist");
      } else {
        toast.error("Failed to add to wishlist");
      }
    }
  };

  const addToCart = async (product) => {
    if (!user) {
      toast.error("Please sign in to add to cart");
      return;
    }
    try {
      await axios.post(`${API}/cart/add`, { 
        product_id: product.id, 
        quantity: 1,
        size: product.sizes?.length > 0 ? product.sizes[0] : null
      });
      toast.success("Added to cart!");
    } catch (error) {
      toast.error("Failed to add to cart");
    }
  };

  const getUniverseName = (id) => {
    const uni = universes.find(u => u.id === id);
    return uni ? uni.name : null;
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#030712]">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-24 pb-12 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <ShoppingBag className="w-8 h-8 text-yellow-500" />
            <h1 className="text-3xl sm:text-4xl font-black text-white font-['Outfit']">{t("store.title")}</h1>
          </div>
          <p className="text-slate-400 max-w-2xl">
            {t("store.subtitle")}
          </p>
          
          {/* Search */}
          <div className="mt-6 max-w-md">
            <Input
              type="text"
              placeholder={t("store.searchProducts")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-dark"
              data-testid="store-search"
            />
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {category === "all" && !universeFilter && featuredProducts.length > 0 && (
        <section className="py-12 border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 mb-6">
              <Star className="w-5 h-5 text-yellow-500" />
              <h2 className="text-xl font-bold text-white font-['Outfit']">{t("store.featuredProducts")}</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {featuredProducts.slice(0, 4).map((product) => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  universeName={getUniverseName(product.universe_id)}
                  isWishlisted={wishlist.includes(product.id)}
                  onWishlist={() => addToWishlist(product.id)}
                  onAddToCart={() => addToCart(product)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Categories & Products */}
      <section className="py-12 flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar Filters */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="sticky top-24">
                <div className="flex items-center gap-2 mb-4">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-medium text-white">Filters</span>
                </div>
                
                {/* Categories */}
                <div className="mb-6">
                  <h3 className="text-xs font-mono tracking-[0.2em] uppercase text-amber-500 mb-3">Categories</h3>
                  <div className="space-y-1">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setSearchParams(cat.id === "all" ? {} : { category: cat.id })}
                        className={`block w-full text-left px-3 py-2 text-sm transition-colors ${
                          category === cat.id 
                            ? "text-yellow-500 bg-yellow-500/10" 
                            : "text-slate-400 hover:text-white hover:bg-white/5"
                        }`}
                        data-testid={`category-${cat.id}`}
                      >
                        {t(cat.labelKey)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Universe Filter */}
                <div>
                  <h3 className="text-xs font-mono tracking-[0.2em] uppercase text-amber-500 mb-3">Universe</h3>
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    <button
                      onClick={() => {
                        const params = new URLSearchParams(searchParams);
                        params.delete("universe");
                        setSearchParams(params);
                      }}
                      className={`block w-full text-left px-3 py-2 text-sm transition-colors ${
                        !universeFilter ? "text-yellow-500 bg-yellow-500/10" : "text-slate-400 hover:text-white hover:bg-white/5"
                      }`}
                    >
                      All Universes
                    </button>
                    {universes.map((uni) => (
                      <button
                        key={uni.id}
                        onClick={() => {
                          const params = new URLSearchParams(searchParams);
                          params.set("universe", uni.id);
                          setSearchParams(params);
                        }}
                        className={`block w-full text-left px-3 py-2 text-sm transition-colors ${
                          universeFilter === uni.id 
                            ? "text-yellow-500 bg-yellow-500/10" 
                            : "text-slate-400 hover:text-white hover:bg-white/5"
                        }`}
                      >
                        {uni.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </aside>

            {/* Products Grid */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-6">
                <span className="text-slate-400 text-sm">{filteredProducts.length} products</span>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="spinner"></div>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-20">
                  <ShoppingBag className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">{t("store.noProducts")}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4" data-testid="products-grid">
                  {filteredProducts.map((product) => (
                    <ProductCard 
                      key={product.id} 
                      product={product}
                      universeName={getUniverseName(product.universe_id)}
                      isWishlisted={wishlist.includes(product.id)}
                      onWishlist={() => addToWishlist(product.id)}
                      onAddToCart={() => addToCart(product)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const ProductCard = ({ product, universeName, isWishlisted, onWishlist, onAddToCart }) => {
  const [imgIndex, setImgIndex] = useState(0);
  const { t } = useTranslation();
  const allImages = [product.image, ...(product.images || [])].filter(Boolean);
  const hasMultiple = allImages.length > 1;

  return (
    <div className="card-surface overflow-hidden group" data-testid={`product-card-${product.id}`}>
      <Link to={`/store/product/${product.id}`}>
        <div className="relative aspect-square overflow-hidden">
          <img 
            src={allImages[imgIndex] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=500"}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          {product.featured && (
            <span className="absolute top-2 left-2 bg-yellow-500 text-black text-xs font-semibold px-2 py-1 uppercase">
              Featured
            </span>
          )}
          {product.stock === 0 && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <span className="text-white font-semibold">{t("store.outOfStock")}</span>
            </div>
          )}
          {/* Dot indicators for multiple images */}
          {hasMultiple && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {allImages.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.preventDefault(); setImgIndex(i); }}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    i === imgIndex ? "bg-amber-500 w-3" : "bg-white/50"
                  }`}
                  data-testid={`product-img-dot-${i}`}
                />
              ))}
            </div>
          )}
        </div>
      </Link>
      
      <div className="p-4">
        {universeName && (
          <span className="text-xs text-amber-500 font-mono uppercase">{universeName}</span>
        )}
        <Link to={`/store/product/${product.id}`}>
          <h3 className="text-sm font-medium text-white mt-1 group-hover:text-yellow-500 transition-colors line-clamp-2">
            {product.name}
          </h3>
        </Link>
        <div className="flex items-center justify-between mt-3">
          <span className="text-lg font-bold text-white">R{product.price.toFixed(2)}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={onWishlist}
              className={`p-2 transition-colors ${isWishlisted ? "text-red-500" : "text-slate-400 hover:text-red-500"}`}
              data-testid={`wishlist-${product.id}`}
            >
              <Heart className={`w-4 h-4 ${isWishlisted ? "fill-current" : ""}`} />
            </button>
            <button
              onClick={onAddToCart}
              disabled={product.stock === 0}
              className="p-2 text-slate-400 hover:text-yellow-500 transition-colors disabled:opacity-50"
              data-testid={`add-cart-${product.id}`}
            >
              <ShoppingCart className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorePage;
